// useEsimManager.ts
import { useState, useEffect, useRef, useMemo } from "react";
import {
  Platform,
  Alert,
  AppState,
  AppStateStatus,
  PermissionsAndroid,
} from "react-native";
import ExpoEsimManagerModule, { EsimInfo } from "./ExpoEsimManagerModule";

export enum InstallationStatus {
  NotStarted = "NotStarted",
  Started = "Started",
  Confirming = "Confirming",
  Completed = "Completed",
  Failed = "Failed",
  Cancelled = "Cancelled",
}

export type InstallError = { code?: string; message?: string } | null;

export const useEsimManager = () => {
  const [permissionsGranted, setPermissionsGranted] = useState(false);
  const [installedEsims, setInstalledEsims] = useState<EsimInfo[]>([]);
  const [diagnosticData, setDiagnosticData] = useState<any>(null);
  const [installationStatus, setInstallationStatus] =
    useState<InstallationStatus>(InstallationStatus.NotStarted);
  const [installError, setInstallError] = useState<InstallError>(null);

  const preInstallEsimsRef = useRef<EsimInfo[]>([]);

  useEffect(() => {
    requestPermissions();
  }, []);

  useEffect(() => {
    if (permissionsGranted) checkInstalledEsims();
  }, [permissionsGranted]);

  // Resolve status=Confirming
  useEffect(() => {
    if (installationStatus !== InstallationStatus.Confirming) return;

    verifyInstallation();
    let attempts = 0;
    const id = setInterval(() => {
      if (
        installationStatus !== InstallationStatus.Confirming ||
        attempts >= 4
      ) {
        clearInterval(id);
        return;
      }
      verifyInstallation();
      attempts += 1;
    }, 1500);

    return () => clearInterval(id);
  }, [installationStatus]);

  // AppState listener
  useEffect(() => {
    const onAppStateChange = (state: AppStateStatus) => {
      if (state !== "active") return;
      checkInstalledEsims();
      if (installationStatus === InstallationStatus.Confirming)
        verifyInstallation();
    };

    const sub = AppState.addEventListener("change", onAppStateChange);
    return () => sub.remove();
  }, [installationStatus]);

  const requestPermissions = async () => {
    if (Platform.OS !== "android") {
      setPermissionsGranted(true);
      return;
    }

    try {
      const grants = await PermissionsAndroid.requestMultiple([
        PermissionsAndroid.PERMISSIONS.READ_PHONE_STATE,
        PermissionsAndroid.PERMISSIONS.READ_PHONE_NUMBERS,
      ]);

      const ok =
        grants[PermissionsAndroid.PERMISSIONS.READ_PHONE_STATE] ===
          PermissionsAndroid.RESULTS.GRANTED &&
        grants[PermissionsAndroid.PERMISSIONS.READ_PHONE_NUMBERS] ===
          PermissionsAndroid.RESULTS.GRANTED;

      if (ok) setPermissionsGranted(true);
      else {
        Alert.alert(
          "Permissões necessárias",
          "Precisamos das permissões de telefone para acessar informações do eSIM.",
          [{ text: "OK" }]
        );
      }
    } catch (err) {
      console.warn("Permission request error:", err);
    }
  };

  const checkInstalledEsims = () => {
    const esims = ExpoEsimManagerModule.getInstalledEsims();
    setInstalledEsims(esims);
  };

  const verifyInstallation = () => {
    const before = preInstallEsimsRef.current;
    const after = ExpoEsimManagerModule.getInstalledEsims();

    if (after.length > before.length) {
      setInstallationStatus(InstallationStatus.Completed);
      return;
    }
    const newEsim = after.find(
      (a) => !before.some((b) => b.subscriptionId === a.subscriptionId)
    );
    if (newEsim) setInstallationStatus(InstallationStatus.Completed);
    else setInstallationStatus(InstallationStatus.Failed);
  };

  const installESim = async (activationCode: string) => {
    preInstallEsimsRef.current = ExpoEsimManagerModule.getInstalledEsims();
    setInstallError(null);
    setInstallationStatus(InstallationStatus.Started);

    try {
      await ExpoEsimManagerModule.install({
        activationCode,
      });

      setInstallationStatus(InstallationStatus.Completed);
    } catch (error: any) {
      console.log("eSIM installation error:", error);
      setInstallError({ code: error?.code, message: error?.message });

      switch (error?.code) {
        case "INVALID_ACTIVATION_CODE":
        case "NETWORK_ERROR":
        case "PROFILE_ALREADY_EXISTS":
        case "UNSUPPORTED_ERROR":
          setInstallationStatus(InstallationStatus.Failed);
          break;

        case "USER_CANCELED":
          setInstallationStatus(InstallationStatus.Cancelled);
          break;

        case "AMBIGUOUS_RESULT":
          setInstallationStatus(InstallationStatus.Confirming);
          break;

        default:
          setInstallationStatus(InstallationStatus.Failed);
      }
    }
  };

  const scanQrCode = async () => {
    preInstallEsimsRef.current = ExpoEsimManagerModule.getInstalledEsims();
    setInstallError(null);
    setInstallationStatus(InstallationStatus.Started);

    try {
      await ExpoEsimManagerModule.scanQrCode();

      setInstallationStatus(InstallationStatus.Completed);
    } catch (error: any) {
      console.log("eSIM QR code scan error:", error);
      setInstallError({ code: error?.code, message: error?.message });

      switch (error?.code) {
        case "INVALID_ACTIVATION_CODE":
        case "NETWORK_ERROR":
        case "PROFILE_ALREADY_EXISTS":
        case "UNSUPPORTED_ERROR":
          setInstallationStatus(InstallationStatus.Failed);
          break;

        case "USER_CANCELED":
          setInstallationStatus(InstallationStatus.Cancelled);
          break;

        case "AMBIGUOUS_RESULT":
          setInstallationStatus(InstallationStatus.Confirming);
          break;

        default:
          setInstallationStatus(InstallationStatus.Failed);
      }
    }
  };

  const runDiagnostic = () => {
    try {
      const diagnostic = ExpoEsimManagerModule.diagnosticInfo();
      setDiagnosticData(diagnostic);
    } catch (error) {
      console.log("Diagnostic error:", error);
    }
  };

  const isEsimSupported = useMemo(() => {
    return ExpoEsimManagerModule.isEsimSupported();
  }, []);

  return {
    /* data */
    permissionsGranted,
    installedEsims,
    diagnosticData,
    installationStatus,
    installError,

    /* actions */
    installESim,
    scanQrCode,
    runDiagnostic,
    checkInstalledEsims,
    isEsimSupported,
  };
};
